import React, { useState } from "react";
import { trackFormSubmit } from "../utils/analytics";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Phone, Mail, Clock, MapPin, Send } from "lucide-react";
import Seo from "../components/Seo";
import { filArianeJsonLd } from "../utils/donneesStructurees";
import ChampAntiRobot from "../components/ChampAntiRobot";
import { attributionCourante } from "../utils/attribution";

const Contact = () => {
  const [formData, setFormData] = useState({
    nom: "",
    email: "",
    telephone: "",
    sujet: "",
    message: "",
    website: ""
  });

  const [enCours, setEnCours] = useState(false);
  const [resultat, setResultat] = useState(null);

  // Ce formulaire ouvrait auparavant le logiciel de messagerie du visiteur
  // (mailto:). Sur mobile, et pour tous ceux qui n'ont pas de client mail
  // configuré, le message n'était jamais envoyé et la demande était perdue
  // sans que personne ne le sache. Il passe désormais par l'API, comme les
  // autres formulaires du site.
  const handleSubmit = async (e) => {
    e.preventDefault();
    setEnCours(true);
    setResultat(null);

    try {
      const reponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/send-general-contact`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            attribution: attributionCourante(),
            nom: formData.nom,
            email: formData.email,
            telephone: formData.telephone,
            message: `Sujet : ${formData.sujet || "non précisé"}\n\n${formData.message}`,
            type_demande: "contact_page",
            website: formData.website,
          }),
        }
      );
      const donnees = await reponse.json();

      if (donnees.success) {
        trackFormSubmit("contact");
        setResultat({
          type: "succes",
          message: "Votre message a bien été envoyé. Nous vous répondons sous 24 h.",
        });
        setFormData({ nom: "", email: "", telephone: "", sujet: "", message: "", website: "" });
      } else {
        setResultat({
          type: "erreur",
          message: donnees.message || "L'envoi a échoué. Appelez-nous au 09 39 03 54 44.",
        });
      }
    } catch (erreur) {
      setResultat({
        type: "erreur",
        message:
          "L'envoi a échoué. Vous pouvez nous joindre au 09 39 03 54 44 ou par courriel.",
      });
    } finally {
      setEnCours(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Seo
        titre="Contact | AMO MaPrimeAdapt' Île-de-France"
        description="Contactez notre équipe AMO agréée ANAH au 09 39 03 54 44. Réponse sous 24 h pour votre projet MaPrimeAdapt' en Île-de-France."
        chemin="/contact"
        donnees={[
          filArianeJsonLd([
            { nom: "Accueil", url: "/" },
            { nom: "Contact", url: "/contact" },
          ]),
        ]}
      />
      <Header />

      <main id="contenu">
      
      <section className="py-20 bg-gradient-to-br from-orange-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Nous contacter
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Besoin de conseils pour votre projet MaPrimeAdapt' ? 
              Notre équipe d'experts vous accompagne dans toutes vos démarches.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-16">
            {/* Informations de contact */}
            <div className="space-y-8">
              <Card className="border-2 border-orange-100">
                <CardHeader>
                  <CardTitle className="text-2xl text-gray-900 mb-4">
                    Informations de contact
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <Phone className="h-6 w-6 text-orange-700 mt-1" />
                    <div>
                      <h2 className="font-semibold text-gray-900">Téléphone</h2>
                      <p className="text-gray-700">09 39 03 54 44</p>
                      <p className="text-sm text-gray-700">Service & appel gratuits</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4">
                    <Mail className="h-6 w-6 text-orange-700 mt-1" />
                    <div>
                      <h2 className="font-semibold text-gray-900">Email</h2>
                      <p className="text-gray-700">amo@transition-energie-france.fr</p>
                      <p className="text-sm text-gray-700">Réponse sous 24h</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <Clock className="h-6 w-6 text-orange-700 mt-1" />
                    <div>
                      <h2 className="font-semibold text-gray-900">Horaires</h2>
                      <p className="text-gray-700">Lun-Jeu : 10h-17h</p>
                      <p className="text-gray-700">Ven : 10h-13h</p>
                      <p className="text-sm text-gray-700">Fermé weekend</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <MapPin className="h-6 w-6 text-orange-700 mt-1" />
                    <div>
                      <h2 className="font-semibold text-gray-900">Zone d'intervention</h2>
                      <p className="text-gray-700">Île-de-France</p>
                      <p className="text-sm text-gray-700">Paris, 77, 91, 92, 93, 95</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="bg-orange-50 p-6 rounded-lg border border-orange-200">
                <h2 className="font-semibold text-gray-900 mb-3">
                  Notre expertise MaPrimeAdapt'
                </h2>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>• Montage complet de votre dossier MaPrimeAdapt'</li>
                  <li>• Accompagnement AMO obligatoire pris en charge</li>
                  <li>• Réseau d'artisans partenaires labellisés</li>
                  <li>• Suivi personnalisé de A à Z</li>
                </ul>
              </div>
            </div>

            {/* Formulaire de contact */}
            <Card className="border-2 border-orange-100">
              <CardHeader>
                <CardTitle className="text-2xl text-gray-900">
                  Formulaire de contact
                </CardTitle>
                <CardDescription>
                  Décrivez-nous votre projet, nous vous répondrons rapidement
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <ChampAntiRobot
                    id="website-page-contact"
                    valeur={formData.website}
                    onChange={handleChange}
                  />
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="nom" className="block text-sm font-medium text-gray-700 mb-2">
                        Nom complet *
                      </label>
                      <Input
                        type="text"
                        name="nom"
                        id="nom"
                        value={formData.nom}
                        onChange={handleChange}
                        required
                        placeholder="Votre nom"
                      />
                    </div>
                    <div>
                      <label htmlFor="telephone" className="block text-sm font-medium text-gray-700 mb-2">
                        Téléphone *
                      </label>
                      <Input
                        type="tel"
                        name="telephone"
                        id="telephone"
                        value={formData.telephone}
                        onChange={handleChange}
                        required
                        placeholder="01 XX XX XX XX"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <Input
                      type="email"
                      name="email"
                      id="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="votre.email@exemple.fr"
                    />
                  </div>

                  <div>
                    <label htmlFor="sujet" className="block text-sm font-medium text-gray-700 mb-2">
                      Sujet de votre demande
                    </label>
                    <select
                      name="sujet"
                      id="sujet"
                      value={formData.sujet}
                      onChange={handleChange}
                      className="w-full h-11 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="">Sélectionnez un sujet</option>
                      <option value="Dossier MaPrimeAdapt'">Dossier MaPrimeAdapt'</option>
                      <option value="Salle de bain">Adaptation salle de bain</option>
                      <option value="Accessibilité">Solutions d'accessibilité</option>
                      <option value="Extérieurs">Aménagements extérieurs</option>
                      <option value="Volets roulants">Volets roulants</option>
                      <option value="Autre">Autre demande</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Votre message *
                    </label>
                    <Textarea
                      name="message"
                      id="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      placeholder="Décrivez votre projet ou votre demande..."
                    />
                  </div>

                  {resultat && (
                    <div
                      role="status"
                      className={`p-4 rounded-lg text-sm ${
                        resultat.type === "succes"
                          ? "bg-green-50 text-green-800 border border-green-200"
                          : "bg-red-50 text-red-800 border border-red-200"
                      }`}
                    >
                      {resultat.message}
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={enCours}
                    className="w-full bg-orange-700 hover:bg-orange-800"
                    size="lg"
                  >
                    <Send className="mr-2 h-5 w-5" />
                    {enCours ? "Envoi en cours..." : "Envoyer le message"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      </main>

      <Footer />
    </div>
  );
};

export default Contact;